import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { api, getUser, setUser, logout, API, ROLE_LABEL } from "../lib/api";
import {
  Users, Trash, PoundSterling, Trash2, ShieldCheck, ShieldX,
  FileText, AlertTriangle, PoundSterling as Pound, Gavel, Ban, RotateCcw, Flag, Wallet,
  MapPinned, BellRing, CheckCircle2, XCircle, Zap, Shield
} from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const navigate = useNavigate();
  const user = getUser();
  const isAdmin = user && user.role === "admin";

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [pendingCarriers, setPendingCarriers] = useState([]);
  const [jobDisputes, setJobDisputes] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [hotspots, setHotspots] = useState([]);
  const [hotspotQuery, setHotspotQuery] = useState("");
  const [stripeHealth, setStripeHealth] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [userReports, setUserReports] = useState([]);
  const [resolvingReport, setResolvingReport] = useState(null);
  const [seedingReviewer, setSeedingReviewer] = useState(false);
  const [resettingReviewer, setResettingReviewer] = useState(false);
  const [samplePost, setSamplePost] = useState({ present: false, post_id: null });
  const [sampleBusy, setSampleBusy] = useState(false);
  const [reuseListings, setReuseListings] = useState([]);
  const [claims, setClaims] = useState([]);

  useEffect(() => {
    // Client-side guard so a logged-out visitor doesn't see a page shell full
    // of 401s. Server enforces the real check via admin_only Bearer JWT dep.
    if (!user) {
      navigate("/login", { replace: true, state: { redirectTo: "/admin" } });
      return;
    }
    if (!isAdmin) {
      navigate("/dashboard", { replace: true });
      return;
    }
    // Validate session against the server — catches stale localStorage after
    // a reseed or a suspended account.
    (async () => {
      try {
        const { data: fresh } = await api.get(`/auth/me/${user.id}`);
        setUser(fresh);
        if (fresh.role !== "admin") {
          navigate("/dashboard", { replace: true });
          return;
        }
        refresh();
      } catch (err) {
        if (err?.response?.status === 404 || err?.response?.status === 403 || err?.response?.status === 401) {
          logout();
          toast.error("Session expired — please sign in again");
          navigate("/login", { replace: true });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render nothing while the guard resolves — prevents the flash-of-admin-UI
  // that would otherwise show 401s on every network tab request.
  if (!user || !isAdmin) return null;

  const refresh = async () => {
    try {
      const [s, u, p, c, jd, pd, rl, sh, rep, ru, cl] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/waste-posts"),
        api.get("/admin/pending-carriers"),
        api.get("/admin/job-disputes"),
        api.get("/admin/payouts-due"),
        api.get("/admin/recruitment-leads", { params: { group_by_area: true } }),
        api.get("/admin/stripe/health").catch(() => ({ data: null })),
        api.get("/admin/reports", { params: { status: "open" } }).catch(() => ({ data: [] })),
        api.get("/admin/reuse-listings").catch(() => ({ data: [] })),
        api.get("/admin/illegal-disposal-claims").catch(() => ({ data: [] })),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setPosts(p.data);
      setPendingCarriers(c.data);
      setJobDisputes(jd.data);
      setPayouts(pd.data);
      setHotspots(rl.data.hotspots || []);
      setStripeHealth(sh.data);
      setUserReports(rep.data || []);
      setReuseListings(ru.data || []);
      setClaims(cl.data || []);
      // First Real Post Kit — non-critical; load in parallel but don't block.
      loadSamplePostStatus();
    } catch (err) {
      if (err?.response?.status === 403 || err?.response?.status === 404) {
        logout();
        toast.error("Session expired — please sign in again");
        navigate("/login");
      } else {
        toast.error("Failed to load admin data");
      }
    }
  };

  const approveCarrier = async (cid) => {
    await api.post(`/admin/carriers/${cid}/approve`);
    toast.success("Carrier approved");
    refresh();
  };

  const rejectCarrier = async (cid) => {
    const reason = window.prompt("Reason for rejection (shown to carrier):");
    if (!reason || !reason.trim()) return;
    await api.post(`/admin/carriers/${cid}/reject`, { reason: reason.trim() });
    toast.success("Carrier rejected");
    refresh();
  };

  const resolveReport = async (reportId, action) => {
    const note = window.prompt(
      action === "resolve"
        ? "What action was taken? (e.g. warned user, removed content)"
        : "Why is this being dismissed? (e.g. not a policy violation)"
    );
    if (note === null) return; // user cancelled
    setResolvingReport(reportId);
    try {
      await api.post(`/admin/reports/${reportId}/resolve`, { action, note: note || null });
      toast.success(action === "resolve" ? "Report resolved" : "Report dismissed");
      setUserReports((prev) => prev.filter((r) => r.id !== reportId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setResolvingReport(null);
    }
  };

  const resolveJobDispute = async (sessionId, action) => {
    const note = window.prompt(`Resolution note (will be visible in audit trail):`) || "";
    try {
      await api.post(`/admin/job-disputes/${sessionId}/resolve`, { action, note });
      toast.success(action === "refund" ? "Refunded homeowner" : "Released to carrier");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const markPaid = async (carrierId, name, totalDue) => {
    const ref = window.prompt(
      `Mark £${totalDue.toFixed(2)} as paid out to ${name}?\n\nOptional: enter your bank-transfer reference for the audit trail.`
    );
    if (ref === null) return;
    try {
      const { data } = await api.post(
        `/admin/payouts/${carrierId}/mark-paid`,
        { payout_reference: (ref || "").trim() }
      );
      toast.success(`Marked ${data.marked} transaction${data.marked === 1 ? "" : "s"} as paid out`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const strikeUser = async (uid) => {
    if (!window.confirm("Add a strike to this user? 3 strikes auto-suspends the account.")) return;
    try {
      const { data } = await api.post(`/admin/users/${uid}/strike`);
      toast.success(`Strike added (${data.strikes}/3${data.suspended ? " — account suspended" : ""})`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const toggleSuspend = async (u) => {
    const path = u.suspended ? "unsuspend" : "suspend";
    if (!window.confirm(`${u.suspended ? "Reinstate" : "Suspend"} ${u.name}?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/${path}`);
      toast.success(`User ${u.suspended ? "reinstated" : "suspended"}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const toggleAdmin = async (u) => {
    const nextIsAdmin = u.role !== "admin";
    const action = nextIsAdmin ? "promote" : "revoke admin from";
    if (!window.confirm(`${nextIsAdmin ? "Promote" : "Revoke admin from"} ${u.name} (${u.email})?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/set-admin`, { is_admin: nextIsAdmin });
      toast.success(`User ${nextIsAdmin ? "promoted to admin" : "demoted"}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Permanently delete this user?")) return;
    await api.delete(`/admin/users/${id}`);
    toast.success("User removed");
    refresh();
  };

  const deletePost = async (id) => {
    await api.delete(`/waste-posts/${id}`, { params: { user_id: user.id } });
    toast.success("Post removed");
    refresh();
  };

  const actionReuse = async (id, action) => {    let reason = null;
    if (action === "hide") {
      reason = window.prompt("Why hide this listing? (shown to owner)");
      if (reason === null) return;
    }
    if (action === "delete" && !window.confirm("Permanently delete this reuse listing?")) return;
    try {
      await api.patch(`/admin/reuse-listings/${id}`, { action, reason: reason || null });
      toast.success(action === "hide" ? "Listing hidden" : action === "unhide" ? "Listing restored" : "Listing deleted");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const setLeadStatus = async (leadId, status) => {
    try {
      await api.post(`/admin/recruitment-leads/${leadId}/status`, { status });
      toast.success(status === "notified" ? "Marked as notified" : "Lead dismissed");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const actionClaim = async (claimId, status) => {
    const note = window.prompt(`Resolution note for claim ${claimId.slice(0, 8)}:`) || "";
    try {
      await api.patch(`/admin/illegal-disposal-claims/${claimId}`, { status, note });
      toast.success(`Claim marked ${status}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const seedUkDemos = async () => {
    if (seeding) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/demo/seed-uk");
      const netNew =
        (data.users_added || 0) + (data.posts_added || 0) + (data.bins_added || 0);
      if (netNew === 0) {
        toast.success("Demo data already in place — nothing new to add.");
      } else {
        toast.success(
          `Seeded UK demos → +${data.users_added} users, +${data.posts_added} posts, +${data.bins_added} bins across ${data.cities?.length || 0} cities`,
        );
      }
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to seed demos");
    } finally {
      setSeeding(false);
    }
  };

  const seedReviewer = async () => {
    if (seedingReviewer) return;
    setSeedingReviewer(true);
    try {
      const { data } = await api.post("/admin/demo/seed-reviewer");
      if (data.ok === false) {
        toast.error(data.reason || "Seed the UK demos first, then re-run this.");
        return;
      }
      const { user_created, post_created, bid_created, messages_created } = data.seeded || {};
      const newBits = [
        user_created && "user",
        post_created && "post",
        bid_created && "bid",
        messages_created ? `${messages_created} messages` : null,
      ].filter(Boolean);
      toast.success(
        newBits.length === 0
          ? "Reviewer account already in place — nothing new to add."
          : `Reviewer account ready · added ${newBits.join(", ")}`,
      );
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to seed reviewer account");
    } finally {
      setSeedingReviewer(false);
    }
  };

  const resetReviewerTxn = async () => {
    if (resettingReviewer) return;
    setResettingReviewer(true);
    try {
      const { data } = await api.post("/admin/demo/reset-reviewer");
      const { post_reset, bids_reset, payments_deleted, notifications_deleted } = data.reset || {};
      toast.success(
        `Reviewer transaction reversed · post:${post_reset} bids:${bids_reset} payments:${payments_deleted} notes:${notifications_deleted}`,
      );
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reset reviewer transaction");
    } finally {
      setResettingReviewer(false);
    }
  };

  // First Real Post Kit — one-tap sample post owned by the admin. Fetched
  // once on mount (see refresh()) so the button shows the correct label.
  const loadSamplePostStatus = async () => {
    try {
      const { data } = await api.get("/admin/demo/sample-post");
      setSamplePost({ present: !!data.present, post_id: data.post_id });
    } catch {
      // Non-blocking — the card stays in the "Add" state on failure.
    }
  };

  const addSamplePost = async () => {
    if (sampleBusy) return;
    setSampleBusy(true);
    try {
      const { data } = await api.post("/admin/demo/sample-post");
      setSamplePost({ present: true, post_id: data.post_id });
      toast.success(
        data.already_present
          ? "Sample post already live — visit Marketplace to view it."
          : "Sample post added — the Marketplace now has demo content.",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add sample post");
    } finally {
      setSampleBusy(false);
    }
  };

  const removeSamplePost = async () => {
    if (sampleBusy) return;
    setSampleBusy(true);
    try {
      await api.delete("/admin/demo/sample-post");
      setSamplePost({ present: false, post_id: null });
      toast.success("Sample post removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove sample post");
    } finally {
      setSampleBusy(false);
    }
  };

  const filteredHotspots = hotspots
    .map((h) => {
      const q = hotspotQuery.trim().toLowerCase();
      if (!q) return h;
      const matches = h.leads.filter(
        (l) =>
          (l.contact || "").toLowerCase().includes(q) ||
          (l.area_label || "").toLowerCase().includes(q)
      );
      if ((h.area_label || "").toLowerCase().includes(q)) return h;
      if (matches.length === 0) return null;
      return { ...h, leads: matches, count: matches.length };
    })
    .filter(Boolean);
  const totalLeads = hotspots.reduce((s, h) => s + h.count, 0);

  if (!user || user.role !== "admin" || !stats) return null;

  const totalOpenDisputes = jobDisputes.length;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] uppercase text-[10px] tracking-wide">Admin panel</Badge>
        <h1 data-testid="admin-title" className="mt-3 text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium" style={{ fontFamily: "Outfit" }}>
          Marketplace overview
        </h1>

        <div className="mt-8 grid md:grid-cols-12 gap-4">
          <Stat className="md:col-span-4" icon={<Users />} label="Users" value={stats.users_total} testId="stat-users" />
          <Stat className="md:col-span-4" icon={<Trash />} label="Removal jobs" value={stats.waste_total} testId="stat-posts" />
          <Stat className="md:col-span-4" icon={<PoundSterling />} label="Gross volume" value={`£${stats.revenue.toFixed(2)}`} testId="stat-revenue" />
          <Card data-testid="stat-platform-fees" className="md:col-span-12 p-6 rounded-2xl border-[#E2DFD8] bg-[#1B3624] text-white flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-xs text-white/60 uppercase tracking-wide">Platform earnings</div>
              <div className="text-4xl font-medium mt-1" style={{ fontFamily: "Outfit" }}>
                £{(stats.platform_fees || 0).toFixed(2)}
              </div>
              <div className="text-xs text-white/60 mt-1">
                {Math.round((stats.fee_rate || 0.05) * 100)}% Binshare commission across all completed transactions
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-white/60 uppercase tracking-wide">Open disputes</div>
              <div className="text-2xl font-medium" style={{ fontFamily: "Outfit" }}>{totalOpenDisputes}</div>
            </div>
          </Card>

          <StripeHealthCard health={stripeHealth} />

          <Card data-testid="admin-data-ops" className="md:col-span-12 p-6 rounded-2xl border-[#E2DFD8] bg-white">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-xs text-[#6B6965] uppercase tracking-wide">Data ops</div>
                <div className="mt-1 font-medium text-lg" style={{ fontFamily: "Outfit" }}>
                  Seed UK-wide demo showcase
                </div>
                <p className="mt-1 text-sm text-[#6B6965] max-w-2xl">
                  Populates the map with sample carriers, waste posts and bin listings across
                  9 UK cities (London → Newcastle). Idempotent — safe to click any time; a
                  second click adds nothing.
                  Demo content is tagged so real bids &amp; rentals are blocked automatically.
                </p>
              </div>
              <Button
                data-testid="admin-seed-uk-btn"
                onClick={seedUkDemos}
                disabled={seeding}
                className="rounded-full bg-[#D67B54] hover:bg-[#c26e49] text-white min-h-[44px] px-6 shrink-0"
              >
                {seeding ? "Seeding…" : "Seed UK demos"}
              </Button>
            </div>

            <div className="mt-6 pt-6 border-t border-[#E2DFD8] flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-xs text-[#6B6965] uppercase tracking-wide">App-Store review</div>
                <div className="mt-1 font-medium text-lg" style={{ fontFamily: "Outfit" }}>
                  Seed Apple reviewer test account
                </div>
                <p className="mt-1 text-sm text-[#6B6965] max-w-2xl">
                  Creates a real login for the App Store reviewer (Guideline 2.1). Includes an
                  active sofa/armchair waste post, a live bid from a demo London carrier, and a
                  pre-populated message thread — so the reviewer can immediately try the
                  Marketplace, Report + Block, and account-owned flows.
                </p>
                <div
                  data-testid="reviewer-credentials"
                  className="mt-3 inline-flex flex-wrap gap-x-4 gap-y-1 text-xs bg-[#F6F4F0] border border-[#E2DFD8] rounded-xl px-3 py-2"
                >
                  <span>
                    <span className="text-[#6B6965]">Email:</span>{" "}
                    <span className="text-[#1B3624] font-mono">apple-reviewer@binshare.co.uk</span>
                  </span>
                  <span>
                    <span className="text-[#6B6965]">Password:</span>{" "}
                    <span className="text-[#1B3624] font-mono">AppleReview2026!</span>
                  </span>
                </div>
                <p className="mt-2 text-[11px] text-[#6B6965]">
                  Requires &ldquo;Seed UK demos&rdquo; to have been run first (bid attaches to the demo London carrier).
                </p>
              </div>
              <Button
                data-testid="admin-seed-reviewer-btn"
                onClick={seedReviewer}
                disabled={seedingReviewer}
                className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white min-h-[44px] px-6 shrink-0"
              >
                {seedingReviewer ? "Seeding…" : "Seed reviewer account"}
              </Button>
              <Button
                data-testid="admin-reset-reviewer-btn"
                onClick={resetReviewerTxn}
                disabled={resettingReviewer}
                variant="outline"
                className="rounded-full border-[#D67B54] text-[#D67B54] hover:bg-[#D67B54]/5 min-h-[44px] px-6 shrink-0"
                title="Reverse the reviewer's accepted bid + delete their payment transactions so the demo can be re-recorded."
              >
                {resettingReviewer ? "Resetting…" : "Reset reviewer transaction"}
              </Button>
            </div>

            {/* First Real Post Kit — one-tap sample post owned by the admin,
                labelled DEMO on cards. Keeps the marketplace from ever
                looking empty during live pitches / video demos. */}
            <div
              data-testid="admin-sample-post-section"
              className="mt-6 pt-6 border-t border-[#E2DFD8] flex flex-wrap items-start justify-between gap-4"
            >
              <div className="min-w-0">
                <div className="text-xs text-[#6B6965] uppercase tracking-wide">First Real Post Kit</div>
                <div className="mt-1 font-medium text-lg" style={{ fontFamily: "Outfit" }}>
                  {samplePost.present ? "Sample post is live" : "Add a sample post"}
                </div>
                <p className="mt-1 text-sm text-[#6B6965] max-w-2xl">
                  One-tap seeder that drops a realistic, admin-owned waste post ("Sofa + 2 armchairs — end of tenancy",
                  London W1G) into the Marketplace. Renders with a <strong>DEMO</strong> pill so real users can tell
                  it apart, and real bids are blocked. Ideal for keeping the map alive during demos.
                </p>
                {samplePost.present && samplePost.post_id && (
                  <p className="mt-2 text-xs text-[#4A6B56]">
                    Live · ref{" "}
                    <a
                      href={`/waste/${samplePost.post_id}`}
                      className="underline hover:text-[#1B3624] font-mono"
                    >
                      {samplePost.post_id.slice(-8)}
                    </a>
                  </p>
                )}
              </div>
              {samplePost.present ? (
                <Button
                  data-testid="admin-sample-post-remove-btn"
                  onClick={removeSamplePost}
                  disabled={sampleBusy}
                  variant="outline"
                  className="rounded-full border-[#6B6965] text-[#6B6965] hover:bg-[#F6F4F0] min-h-[44px] px-6 shrink-0"
                >
                  {sampleBusy ? "Removing…" : "Remove sample post"}
                </Button>
              ) : (
                <Button
                  data-testid="admin-sample-post-add-btn"
                  onClick={addSamplePost}
                  disabled={sampleBusy}
                  className="rounded-full bg-[#4A6B56] hover:bg-[#3d5947] text-white min-h-[44px] px-6 shrink-0"
                >
                  {sampleBusy ? "Adding…" : "Add sample post"}
                </Button>
              )}
            </div>
          </Card>

          <Card className="md:col-span-12 p-6 rounded-2xl border-[#E2DFD8] bg-[#F6F4F0]">
            <div className="text-xs text-[#6B6965] uppercase tracking-wide">Users by role</div>
            <div className="mt-3 flex flex-wrap gap-4">
              {Object.entries(stats.users_by_role).map(([k, v]) => (
                <div key={k} className="bg-white rounded-2xl px-5 py-3 border border-[#E2DFD8]">
                  <div className="text-xs text-[#6B6965] uppercase">{ROLE_LABEL[k]}</div>
                  <div className="text-xl font-medium" style={{ fontFamily: "Outfit" }}>{v}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* PAYOUTS DUE — manual bank transfer ledger */}
        <section className="mt-12">
          <h2 className="text-2xl tracking-tight font-medium mb-4 flex items-center gap-2 flex-wrap" style={{ fontFamily: "Outfit" }}>
            <Wallet className="w-5 h-5 text-[#4A6B56]" /> Payouts due to carriers
            {payouts.length > 0 && (
              <Badge className="bg-[#4A6B56] text-white hover:bg-[#4A6B56] text-[10px] uppercase">
                {payouts.length} owed · £{payouts.reduce((s, p) => s + p.total_due, 0).toFixed(2)}
              </Badge>
            )}
          </h2>
          <p className="text-sm text-[#6B6965] mb-4">
            Carriers whose escrow has cleared but Binshare hasn&apos;t paid them yet. Send a bank transfer to each carrier for the amount shown, then click <strong>Mark paid</strong>.
          </p>
          {payouts.length === 0 ? (
            <Card className="p-10 rounded-2xl border-dashed border-[#E2DFD8] bg-transparent text-center text-[#6B6965]">
              All caught up — no carrier payouts outstanding.
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {payouts.map((p) => (
                <Card key={p.carrier_id} data-testid={`payout-${p.carrier_id}`} className="p-5 rounded-2xl border-[#E2DFD8]">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-medium" style={{ fontFamily: "Outfit" }}>{p.carrier_name}</div>
                      {p.carrier_company && <div className="text-xs text-[#6B6965]">{p.carrier_company}</div>}
                      <div className="text-xs text-[#6B6965] mt-0.5 break-all">{p.carrier_email}</div>
                      <div className="text-xs text-[#6B6965] mt-1">{p.item_count} completed job{p.item_count === 1 ? "" : "s"}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-medium text-[#4A6B56]" style={{ fontFamily: "Outfit" }}>£{p.total_due.toFixed(2)}</div>
                      <div className="text-[10px] text-[#6B6965] uppercase">to pay</div>
                    </div>
                  </div>
                  <details className="mt-3 text-xs text-[#6B6965]">
                    <summary className="cursor-pointer hover:text-[#1B3624] select-none">Breakdown ({p.item_count})</summary>
                    <ul className="mt-2 space-y-1">
                      {p.items.map((it) => (
                        <li key={it.session_id} className="flex justify-between gap-2 py-1 border-t border-[#E2DFD8]">
                          <span className="truncate">{it.post_title || "—"}</span>
                          <span className="text-[#1B3624] font-medium">£{Number(it.amount).toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                  <Button
                    data-testid={`mark-paid-${p.carrier_id}`}
                    onClick={() => markPaid(p.carrier_id, p.carrier_name, p.total_due)}
                    className="mt-4 rounded-full bg-[#4A6B56] hover:bg-[#3d5947] text-white min-h-[44px] w-full sm:w-auto"
                  >
                    <Wallet className="w-4 h-4 mr-1" /> Mark £{p.total_due.toFixed(2)} as paid
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* JOB DISPUTES (carriers↔homeowners) */}
        <section className="mt-12">
          <h2 className="text-2xl tracking-tight font-medium mb-4 flex items-center gap-2 flex-wrap" style={{ fontFamily: "Outfit" }}>
            <Gavel className="w-5 h-5 text-red-600" /> Job disputes
            {jobDisputes.length > 0 && (
              <Badge className="bg-red-100 text-red-800 hover:bg-red-100 text-[10px] uppercase">{jobDisputes.length} open</Badge>
            )}
          </h2>
          {jobDisputes.length === 0 ? (
            <Card className="p-10 rounded-2xl border-dashed border-[#E2DFD8] bg-transparent text-center text-[#6B6965]">
              No active job disputes.
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {jobDisputes.map((d) => (
                <Card key={d.session_id} data-testid={`job-dispute-${d.session_id}`} className="p-5 rounded-2xl border-red-200 bg-red-50/40">
                  <Badge className="bg-red-100 text-red-800 hover:bg-red-100 uppercase text-[10px]">
                    Filed by {d.filed_by_role} · {d.filed_by_name}
                  </Badge>
                  <div className="mt-2 font-medium" style={{ fontFamily: "Outfit" }}>{d.post_title}</div>
                  <div className="text-xs text-[#6B6965] mt-1">
                    Homeowner: <span className="text-[#1B3624] font-medium">{d.homeowner_name}</span> ·
                    Carrier: <span className="text-[#1B3624] font-medium">{d.carrier_name}</span>
                  </div>
                  <div className="text-xs text-[#6B6965] mt-1">Escrow held: £{Number(d.amount).toFixed(2)}</div>
                  <div className="mt-3 text-sm text-red-800 bg-white rounded-xl p-3 border border-red-100">
                    <span className="font-medium">Reason:</span> {d.dispute_reason}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      data-testid={`job-refund-${d.session_id}`}
                      onClick={() => resolveJobDispute(d.session_id, "refund")}
                      className="rounded-full bg-[#D67B54] hover:bg-[#c66c44] text-white min-h-[44px]"
                    >
                      <Pound className="w-4 h-4 mr-1" /> Refund homeowner
                    </Button>
                    <Button
                      data-testid={`job-release-${d.session_id}`}
                      onClick={() => resolveJobDispute(d.session_id, "release")}
                      variant="outline"
                      className="rounded-full border-[#4A6B56] text-[#4A6B56] hover:bg-[#4A6B56] hover:text-white min-h-[44px]"
                    >
                      <ShieldCheck className="w-4 h-4 mr-1" /> Release to carrier
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* USER REPORTS (Guideline 1.2) */}
        <section className="mt-12" data-testid="admin-user-reports">
          <h2 className="text-2xl tracking-tight font-medium mb-4 flex items-center gap-2 flex-wrap" style={{ fontFamily: "Outfit" }}>
            <Flag className="w-5 h-5 text-[#D67B54]" /> User reports
            {userReports.length > 0 && (
              <Badge className="bg-[#D67B54] text-white hover:bg-[#D67B54] text-[10px] uppercase">
                {userReports.length} open
              </Badge>
            )}
          </h2>
          {userReports.length === 0 ? (
            <Card
              data-testid="admin-user-reports-empty"
              className="p-10 rounded-2xl border-dashed border-[#E2DFD8] bg-transparent text-center text-[#6B6965]"
            >
              No open user reports.
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {userReports.map((r) => (
                <Card
                  key={r.id}
                  data-testid={`user-report-${r.id}`}
                  className="p-5 rounded-2xl border-[#E2DFD8]"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] uppercase text-[10px]">
                      {r.target_kind}
                    </Badge>
                    <Badge className="bg-red-100 text-red-800 hover:bg-red-100 uppercase text-[10px]">
                      {r.reason}
                    </Badge>
                    <span className="text-[11px] text-[#6B6965] ml-auto">
                      {new Date(r.created_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-[#6B6965]">
                    Reporter: <span className="text-[#1B3624] font-medium">{r.reporter_name || r.reporter_id}</span>
                  </div>
                  <div className="mt-1 text-xs text-[#6B6965] break-all">
                    Target id: <span className="text-[#1B3624]">{r.target_id}</span>
                  </div>
                  {r.details && (
                    <div className="mt-3 text-sm text-[#1A1A1A] bg-[#F6F4F0] rounded-xl p-3 border border-[#E2DFD8] whitespace-pre-wrap">
                      {r.details}
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      data-testid={`report-resolve-${r.id}`}
                      disabled={resolvingReport === r.id}
                      onClick={() => resolveReport(r.id, "resolve")}
                      className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white min-h-[40px]"
                      size="sm"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Resolve
                    </Button>
                    <Button
                      data-testid={`report-dismiss-${r.id}`}
                      disabled={resolvingReport === r.id}
                      onClick={() => resolveReport(r.id, "dismiss")}
                      variant="outline"
                      className="rounded-full min-h-[40px]"
                      size="sm"
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Dismiss
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* CARRIER VERIFICATIONS */}
        <section className="mt-12">
          <h2 className="text-2xl tracking-tight font-medium mb-4 flex items-center gap-2 flex-wrap" style={{ fontFamily: "Outfit" }}>
            <ShieldCheck className="w-5 h-5 text-[#D67B54]" /> Carrier verifications
            {pendingCarriers.length > 0 && (
              <Badge className="bg-[#D67B54] text-white hover:bg-[#D67B54] text-[10px] uppercase">
                {pendingCarriers.filter((c) => c.license_status === "pending").length} pending
              </Badge>
            )}
          </h2>
          {pendingCarriers.length === 0 ? (
            <Card className="p-10 rounded-2xl border-dashed border-[#E2DFD8] bg-transparent text-center text-[#6B6965]">
              No carriers awaiting verification.
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {pendingCarriers.map((c) => (
                <Card key={c.id} data-testid={`pending-carrier-${c.id}`} className="p-5 rounded-2xl border-[#E2DFD8]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium" style={{ fontFamily: "Outfit" }}>{c.name}</div>
                    <Badge className={`uppercase text-[10px] ${c.license_status === "pending" ? "bg-[#D67B54] text-white" : "bg-red-500 text-white"}`}>
                      {c.license_status}
                    </Badge>
                  </div>
                  <div className="text-xs text-[#6B6965] mt-1">{c.email}</div>
                  <div className="text-xs text-[#6B6965] mt-1">Licence: <span className="text-[#1B3624] font-medium">{c.license_no || "—"}</span></div>
                  {c.company && <div className="text-xs text-[#6B6965] mt-1">{c.company}</div>}
                  {c.license_reject_reason && (
                    <div className="text-xs text-red-700 mt-1">Previous reason: {c.license_reject_reason}</div>
                  )}
                  {c.license_document_url && (
                    <a
                      href={`${API.replace(/\/api$/, "")}${c.license_document_url}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`view-doc-${c.id}`}
                      className="mt-2 inline-flex items-center gap-1 text-xs text-[#4A6B56] hover:underline"
                    >
                      <FileText className="w-3 h-3" /> View licence document
                    </a>
                  )}
                  {/* Task 3 — carrier readiness fields */}
                  <div className="mt-3 space-y-1 text-xs text-[#6B6965] border-t border-[#F0EEE8] pt-3">
                    <div data-testid={`admin-insurance-${c.id}`}>
                      <span className="text-[#1B3624] font-medium">PL insurance:</span>{" "}
                      {c.insurance_document_url ? (
                        <a
                          href={`${API.replace(/\/api$/, "")}${c.insurance_document_url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#4A6B56] underline"
                        >
                          On file
                        </a>
                      ) : (
                        <span className="text-red-700">Missing</span>
                      )}
                    </div>
                    <div data-testid={`admin-facility-${c.id}`}>
                      <span className="text-[#1B3624] font-medium">Disposal facility:</span>{" "}
                      {c.disposal_facility_name || c.disposal_facility_permit ? (
                        <span>{c.disposal_facility_name || "—"}{c.disposal_facility_permit ? ` · Permit ${c.disposal_facility_permit}` : ""}</span>
                      ) : (
                        <span className="text-red-700">Missing</span>
                      )}
                    </div>
                    <div data-testid={`admin-vehicles-${c.id}`}>
                      <span className="text-[#1B3624] font-medium">Vehicles:</span>{" "}
                      {(c.vehicle_types || []).length > 0 ? (c.vehicle_types || []).join(", ") : <span className="text-red-700">Missing</span>}
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      data-testid={`approve-carrier-${c.id}`}
                      onClick={() => approveCarrier(c.id)}
                      className="rounded-full bg-[#4A6B56] hover:bg-[#3d5947] text-white min-h-[44px]"
                    >
                      <ShieldCheck className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button
                      data-testid={`reject-carrier-${c.id}`}
                      onClick={() => rejectCarrier(c.id)}
                      variant="outline"
                      className="rounded-full border-red-200 text-red-700 hover:bg-red-50 min-h-[44px]"
                    >
                      <ShieldX className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* WAITING LIST HOTSPOTS — where demand exists but coverage doesn't */}
        <section className="mt-12" data-testid="hotspots-section">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <div className="text-xs uppercase tracking-[0.14em] text-[#D67B54] font-medium">Recruitment</div>
              <h2 className="text-2xl tracking-tight font-medium mt-1" style={{ fontFamily: "Outfit" }}>
                Waiting list hotspots
              </h2>
              <p className="text-sm text-[#6B6965] mt-1 max-w-xl">
                Areas where visitors have asked to be notified when a licensed carrier launches. Sorted by demand.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Badge className="bg-[#1B3624] text-white hover:bg-[#1B3624] uppercase text-[10px]" data-testid="hotspots-total">
                {totalLeads} lead{totalLeads === 1 ? "" : "s"} · {hotspots.length} area{hotspots.length === 1 ? "" : "s"}
              </Badge>
              <input
                data-testid="hotspots-search"
                type="text"
                value={hotspotQuery}
                onChange={(e) => setHotspotQuery(e.target.value)}
                placeholder="Filter by area or contact…"
                className="rounded-full border border-[#E2DFD8] bg-white px-4 py-2 text-sm min-w-[240px] outline-none focus:border-[#1B3624]"
              />
            </div>
          </div>

          {hotspots.length === 0 ? (
            <Card className="p-8 rounded-2xl border-dashed border-[#E2DFD8] bg-transparent text-center">
              <MapPinned className="w-6 h-6 mx-auto text-[#6B6965]" />
              <div className="mt-3 text-sm text-[#6B6965]">
                No waiting-list signups yet — the callout appears on the Marketplace when a search falls outside every carrier&apos;s coverage circle.
              </div>
            </Card>
          ) : filteredHotspots.length === 0 ? (
            <Card className="p-8 rounded-2xl border-dashed border-[#E2DFD8] bg-transparent text-center text-sm text-[#6B6965]">
              No hotspots match &ldquo;{hotspotQuery}&rdquo;.
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {filteredHotspots.map((h) => (
                <Card
                  key={h.area_label}
                  data-testid={`hotspot-${h.area_label}`}
                  className="p-5 rounded-2xl border-[#E2DFD8] bg-white"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <MapPinned className="w-4 h-4 text-[#4A6B56] shrink-0" />
                        <div className="font-medium truncate" style={{ fontFamily: "Outfit" }}>
                          {h.area_label}
                        </div>
                      </div>
                      {h.lat != null && (
                        <div className="text-[11px] text-[#6B6965] mt-1 ml-6">
                          {h.lat.toFixed(3)}, {h.lng.toFixed(3)}
                        </div>
                      )}
                    </div>
                    <Badge className="bg-[#D67B54] text-white hover:bg-[#D67B54] text-xs shrink-0">
                      {h.count} lead{h.count === 1 ? "" : "s"}
                    </Badge>
                  </div>

                  <div className="mt-4 space-y-2">
                    {h.leads.map((l) => (
                      <div
                        key={l.id}
                        data-testid={`hotspot-lead-${l.id}`}
                        className="flex items-center justify-between gap-3 rounded-xl border border-[#E2DFD8] px-3 py-2"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${l.contact_type === "phone" ? "bg-[#EBE8E0] text-[#1B3624]" : "bg-[#4A6B56]/15 text-[#4A6B56]"}`}>
                              {l.contact_type}
                            </span>
                            <span className="text-sm font-medium truncate">{l.contact}</span>
                          </div>
                          <div className="text-[11px] text-[#6B6965] mt-0.5 flex items-center gap-2">
                            <span>{new Date(l.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
                            {l.status !== "pending" && (
                              <span className={`uppercase tracking-wide ${l.status === "notified" ? "text-[#4A6B56]" : "text-[#6B6965]"}`}>
                                · {l.status}
                              </span>
                            )}
                          </div>
                        </div>
                        {l.status === "pending" && (
                          <div className="flex gap-1 shrink-0">
                            <Button
                              data-testid={`lead-notify-${l.id}`}
                              variant="ghost"
                              size="icon"
                              onClick={() => setLeadStatus(l.id, "notified")}
                              title="Mark as notified"
                              className="text-[#4A6B56] hover:text-[#3d5947] hover:bg-[#4A6B56]/10 min-h-[36px] min-w-[36px]"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                            <Button
                              data-testid={`lead-dismiss-${l.id}`}
                              variant="ghost"
                              size="icon"
                              onClick={() => setLeadStatus(l.id, "dismissed")}
                              title="Dismiss"
                              className="text-[#6B6965] hover:text-red-600 min-h-[36px] min-w-[36px]"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        {l.status === "notified" && (
                          <BellRing className="w-4 h-4 text-[#4A6B56] shrink-0" title="Notified" />
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* USERS — strike/suspend */}
        <section className="mt-12">
          <h2 className="text-2xl tracking-tight font-medium mb-4" style={{ fontFamily: "Outfit" }}>Users</h2>
          <Card className="rounded-2xl border-[#E2DFD8] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F6F4F0] text-left text-[#6B6965] uppercase text-[11px] tracking-wide">
                  <tr>
                    <th className="p-4">Name</th>
                    <th className="p-4">Email</th>
                    <th className="p-4 hidden md:table-cell">Phone</th>
                    <th className="p-4">Role</th>
                    <th className="p-4">Strikes</th>
                    <th className="p-4">Status</th>
                    <th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`admin-user-${u.id}`} className="border-t border-[#E2DFD8]">
                      <td className="p-4 font-medium">{u.name}</td>
                      <td data-testid={`user-email-${u.id}`} className="p-4 text-[#6B6965] break-all">{u.email}</td>
                      <td data-testid={`user-phone-${u.id}`} className="p-4 text-[#6B6965] hidden md:table-cell whitespace-nowrap">
                        {u.phone ? (
                          <a href={`tel:${u.phone}`} className="text-[#1B3624] hover:text-[#D67B54]">{u.phone}</a>
                        ) : (
                          <span className="text-[#B7B4AC]">—</span>
                        )}
                      </td>
                      <td className="p-4"><Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] uppercase text-[10px]">{ROLE_LABEL[u.role]}</Badge>
                        {u.role === "carrier" && (
                          <div data-testid={`admin-carrier-compliance-${u.id}`} className="mt-1 text-[10px] text-[#6B6965] leading-tight">
                            <div>PL: {u.insurance_document_url ? <span className="text-[#4A6B56]">✓</span> : <span className="text-red-700">✗</span>}
                              {" · "}Facility: {(u.disposal_facility_name && u.disposal_facility_permit) ? <span className="text-[#4A6B56]">✓</span> : <span className="text-red-700">✗</span>}
                              {" · "}Vehicles: {(u.vehicle_types || []).length > 0 ? <span className="text-[#4A6B56]">{(u.vehicle_types || []).length}</span> : <span className="text-red-700">0</span>}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <span data-testid={`strikes-${u.id}`} className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium ${(u.strikes || 0) >= 2 ? "bg-red-100 text-red-700" : (u.strikes || 0) >= 1 ? "bg-[#D67B54]/20 text-[#D67B54]" : "bg-[#EBE8E0] text-[#6B6965]"}`}>
                          {u.strikes || 0}
                        </span>
                      </td>
                      <td className="p-4">
                        {u.suspended ? (
                          <Badge data-testid={`suspended-${u.id}`} className="bg-red-100 text-red-800 hover:bg-red-100 uppercase text-[10px]">Suspended</Badge>
                        ) : (
                          <Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] uppercase text-[10px]">Active</Badge>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <div className="inline-flex gap-1 flex-wrap justify-end">
                          {u.id !== user.id && (
                            <Button
                              data-testid={`toggle-admin-${u.id}`}
                              variant="ghost"
                              size="icon"
                              onClick={() => toggleAdmin(u)}
                              title={u.role === "admin" ? "Revoke admin" : "Promote to admin"}
                              className={`min-h-[40px] min-w-[40px] ${u.role === "admin" ? "text-[#D67B54] hover:text-[#c26e49]" : "text-[#6B6965] hover:text-[#4A6B56]"}`}
                            >
                              <Shield className="w-4 h-4" />
                            </Button>
                          )}
                          {u.id !== user.id && u.role !== "admin" && (
                            <>
                              <Button
                                data-testid={`strike-${u.id}`}
                                variant="ghost"
                                size="icon"
                                onClick={() => strikeUser(u.id)}
                                title="Add strike"
                                className="text-[#6B6965] hover:text-[#D67B54] min-h-[40px] min-w-[40px]"
                              >
                                <Flag className="w-4 h-4" />
                              </Button>
                              <Button
                                data-testid={`suspend-${u.id}`}
                                variant="ghost"
                                size="icon"
                                onClick={() => toggleSuspend(u)}
                                title={u.suspended ? "Reinstate" : "Suspend"}
                                className={`min-h-[40px] min-w-[40px] ${u.suspended ? "text-[#4A6B56] hover:text-[#3d5947]" : "text-[#6B6965] hover:text-red-600"}`}
                              >
                                {u.suspended ? <RotateCcw className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                              </Button>
                            </>
                          )}
                          {u.id !== user.id && (
                            <Button
                              data-testid={`delete-user-${u.id}`}
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteUser(u.id)}
                              title="Delete"
                              className="text-[#6B6965] hover:text-red-600 min-h-[40px] min-w-[40px]"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>

        <section className="mt-12">
          <h2 className="text-2xl tracking-tight font-medium mb-4" style={{ fontFamily: "Outfit" }}>All removal jobs</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {posts.map((p) => (
              <Card key={p.id} data-testid={`admin-post-${p.id}`} className="p-5 rounded-2xl border-[#E2DFD8]">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] uppercase text-[10px]">{p.waste_type}</Badge>
                    <div className="mt-2 font-medium" style={{ fontFamily: "Outfit" }}>{p.title}</div>
                    <div className="text-xs text-[#6B6965] mt-1">{p.address}</div>
                    <div className="text-xs text-[#6B6965] mt-1">by {p.user_name}</div>
                  </div>
                  <Button
                    data-testid={`admin-delete-post-${p.id}`}
                    variant="ghost"
                    size="icon"
                    onClick={() => deletePost(p.id)}
                    className="text-[#6B6965] hover:text-red-600 min-h-[40px] min-w-[40px]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                {p.access_details && (
                  <div data-testid={`admin-post-access-${p.id}`} className="mt-3 rounded-xl border border-[#D67B54]/25 bg-[#D67B54]/5 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-[#D67B54] font-medium">Access</div>
                    <p className="mt-1 text-xs text-[#1B3624] leading-snug whitespace-pre-wrap">{p.access_details}</p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </section>

        {/* Phase 3 — Reuse moderation (reported/flagged first). */}
        <section data-testid="admin-reuse-section" className="mt-12">
          <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
            <h2 className="text-2xl tracking-tight font-medium flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              Reuse listings
              {reuseListings.some((l) => (l.reports_count || 0) > 0) && (
                <Badge className="bg-red-100 text-red-800 hover:bg-red-100 uppercase text-[10px]">
                  {reuseListings.filter((l) => (l.reports_count || 0) > 0).length} flagged
                </Badge>
              )}
            </h2>
            <span data-testid="admin-reuse-count" className="text-xs uppercase tracking-widest text-[#6B6965]">
              {reuseListings.length} total
            </span>
          </div>
          {reuseListings.length === 0 ? (
            <Card className="p-8 rounded-2xl border-dashed border-[#E2DFD8] bg-transparent text-center text-[#6B6965]">
              No reuse listings yet.
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reuseListings.map((l) => (
                <Card key={l.id} data-testid={`admin-reuse-${l.id}`} className="p-5 rounded-2xl border-[#E2DFD8]">
                  {l.image_urls?.[0] && (
                    <img src={l.image_urls[0]} alt="" className="w-full h-32 rounded-xl object-cover mb-3" />
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-[#EBE8E0] text-[#1B3624] uppercase text-[10px]">{l.category}</Badge>
                    <Badge className={`uppercase text-[10px] ${l.status === "hidden" ? "bg-red-100 text-red-700" : l.status === "taken" ? "bg-[#1B3624] text-white" : l.status === "reserved" ? "bg-[#D67B54] text-white" : "bg-[#4A6B56] text-white"}`}>
                      {l.status}
                    </Badge>
                    {(l.reports_count || 0) > 0 && (
                      <Badge data-testid={`admin-reuse-reports-${l.id}`} className="bg-red-600 text-white uppercase text-[10px]">
                        {l.reports_count} reports
                      </Badge>
                    )}
                  </div>
                  <div className="mt-2 font-medium" style={{ fontFamily: "Outfit" }}>{l.title}</div>
                  <div className="text-xs text-[#6B6965] mt-1">
                    Owner: <span className="text-[#1B3624] font-medium">{l.owner_name}</span> · {l.postcode_area}
                  </div>
                  <div className="text-xs text-[#6B6965] mt-1 line-clamp-2">{l.description}</div>
                  {l.hidden_reason && (
                    <div className="mt-2 text-xs text-red-800 bg-red-50 rounded-xl p-2 border border-red-100">
                      Hidden: {l.hidden_reason}
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <a
                      href={`/reuse/${l.id}`}
                      className="text-xs text-[#4A6B56] hover:underline font-medium py-2"
                      data-testid={`admin-reuse-view-${l.id}`}
                    >
                      View →
                    </a>
                    {l.status !== "hidden" ? (
                      <Button
                        data-testid={`admin-reuse-hide-${l.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => actionReuse(l.id, "hide")}
                        className="rounded-full border-[#D67B54] text-[#D67B54] hover:bg-[#D67B54] hover:text-white"
                      >
                        <ShieldX className="w-4 h-4 mr-1" /> Hide
                      </Button>
                    ) : (
                      <Button
                        data-testid={`admin-reuse-unhide-${l.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => actionReuse(l.id, "unhide")}
                        className="rounded-full border-[#4A6B56] text-[#4A6B56] hover:bg-[#4A6B56] hover:text-white"
                      >
                        <ShieldCheck className="w-4 h-4 mr-1" /> Restore
                      </Button>
                    )}
                    <Button
                      data-testid={`admin-reuse-delete-${l.id}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => actionReuse(l.id, "delete")}
                      className="rounded-full text-[#6B6965] hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>
        {/* Phase 5 — illegal disposal claims (£500 Flytipping Guarantee) */}
        <section data-testid="admin-claims-section" className="mt-12">
          <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
            <h2 className="text-2xl tracking-tight font-medium flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              Illegal disposal claims
              {claims.some((c) => c.status === "open") && (
                <Badge className="bg-red-100 text-red-800 hover:bg-red-100 uppercase text-[10px]">
                  {claims.filter((c) => c.status === "open").length} open
                </Badge>
              )}
            </h2>
            <span data-testid="admin-claims-count" className="text-xs uppercase tracking-widest text-[#6B6965]">
              {claims.length} total
            </span>
          </div>
          {claims.length === 0 ? (
            <Card className="p-8 rounded-2xl border-dashed border-[#E2DFD8] bg-transparent text-center text-[#6B6965]">
              No claims filed under the £500 Flytipping Guarantee.
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {claims.map((c) => (
                <Card key={c.id} data-testid={`admin-claim-${c.id}`} className="p-5 rounded-2xl border-[#E2DFD8]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`uppercase text-[10px] ${c.status === "open" ? "bg-red-100 text-red-800" : c.status === "in_review" ? "bg-[#D67B54] text-white" : c.status === "resolved" ? "bg-[#4A6B56] text-white" : "bg-[#EBE8E0] text-[#6B6965]"}`}>
                      {c.status}
                    </Badge>
                    {c.waste_post_id && (
                      <a href={`/waste/${c.waste_post_id}`} className="text-xs text-[#4A6B56] hover:underline">Job {c.waste_post_id.slice(0, 8).toUpperCase()}</a>
                    )}
                  </div>
                  <div className="mt-2 text-sm">
                    <span className="text-[#6B6965]">From </span>
                    <span className="font-medium">{c.customer_name || c.customer_email}</span>
                  </div>
                  <p className="mt-2 text-sm text-[#1A1A1A] whitespace-pre-wrap line-clamp-4">{c.description}</p>
                  {c.resolution_note && (
                    <div className="mt-2 text-xs text-[#4A6B56] bg-[#F6F4F0] rounded-xl p-2">Resolution: {c.resolution_note}</div>
                  )}
                  {c.status !== "resolved" && c.status !== "rejected" && (
                    <div className="mt-3 flex gap-2 flex-wrap">
                      {c.status === "open" && (
                        <Button data-testid={`admin-claim-review-${c.id}`} size="sm" variant="outline" onClick={() => actionClaim(c.id, "in_review")} className="rounded-full">Start review</Button>
                      )}
                      <Button data-testid={`admin-claim-resolve-${c.id}`} size="sm" onClick={() => actionClaim(c.id, "resolved")} className="rounded-full bg-[#4A6B56] hover:bg-[#3d5947] text-white">Resolve</Button>
                      <Button data-testid={`admin-claim-reject-${c.id}`} size="sm" variant="ghost" onClick={() => actionClaim(c.id, "rejected")} className="rounded-full text-[#6B6965]">Reject</Button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}

function Stat({ icon, label, value, className, testId }) {
  return (
    <Card data-testid={testId} className={`${className} p-6 rounded-2xl border-[#E2DFD8] bg-white`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#EBE8E0] flex items-center justify-center text-[#1B3624]">{icon}</div>
        <div>
          <div className="text-xs text-[#6B6965] uppercase tracking-wide">{label}</div>
          <div className="text-3xl font-medium mt-0.5" style={{ fontFamily: "Outfit" }}>{value}</div>
        </div>
      </div>
    </Card>
  );
}

function StripeHealthCard({ health }) {
  if (!health) {
    return (
      <Card className="md:col-span-12 p-5 rounded-2xl border-dashed border-[#E2DFD8] bg-transparent text-sm text-[#6B6965]" data-testid="stripe-health-loading">
        Loading Stripe status…
      </Card>
    );
  }
  const { mode, webhook_secret_configured, platform_account, carriers, warnings, ready_for_live } = health;
  const isLive = mode === "live";
  const isReady = ready_for_live;

  // Traffic-light: red if unconfigured or live-with-warnings, amber if test,
  // green if live and healthy. Colours picked from the app palette.
  const tone = mode === "unconfigured"
    ? { bg: "#8B2C0F", chip: "#F5A971", label: "Not configured" }
    : isLive && isReady
      ? { bg: "#1B3624", chip: "#4A6B56", label: "LIVE · Ready" }
      : isLive
        ? { bg: "#8B2C0F", chip: "#F5A971", label: "LIVE · Needs attention" }
        : { bg: "#4A6B56", chip: "#EBE8E0", label: "Test mode" };

  return (
    <Card
      data-testid="stripe-health-card"
      className="md:col-span-12 p-6 rounded-2xl text-white flex flex-col gap-4"
      style={{ backgroundColor: tone.bg }}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs text-white/60 uppercase tracking-wide flex items-center gap-2">
            <Zap className="w-3.5 h-3.5" /> Stripe payments
          </div>
          <div className="mt-1 flex items-center gap-3">
            <span
              data-testid="stripe-mode-badge"
              className="inline-flex items-center gap-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.14em] px-2 py-1"
              style={{ backgroundColor: tone.chip, color: "#1B3624" }}
            >
              {tone.label}
            </span>
            {platform_account?.default_currency && (
              <span className="text-[11px] text-white/70">
                {platform_account.default_currency} · {platform_account.country || "?"}
              </span>
            )}
          </div>
        </div>
        <div className="text-right text-[11px] leading-tight">
          <div className="text-white/60 uppercase tracking-wide">Carriers connected</div>
          <div className="text-2xl font-medium" style={{ fontFamily: "Outfit" }}>
            {carriers?.connected ?? 0}<span className="text-white/50 text-base"> / {carriers?.total ?? 0}</span>
          </div>
          <div className="text-white/60 mt-0.5">{carriers?.payouts_enabled ?? 0} payout-ready</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <HealthCheck ok={mode !== "unconfigured"} label="API key set" />
        <HealthCheck ok={webhook_secret_configured} label="Webhook secret" />
        <HealthCheck ok={platform_account?.charges_enabled} label="Charges enabled" />
        <HealthCheck ok={platform_account?.payouts_enabled} label="Payouts enabled" />
        <HealthCheck ok={platform_account?.details_submitted} label="Business verified" />
        <HealthCheck ok={(platform_account?.default_currency || "").toUpperCase() === "GBP"} label="Currency GBP" />
      </div>

      {warnings?.length > 0 && (
        <div data-testid="stripe-warnings" className="rounded-xl bg-black/25 border border-white/10 p-3 text-[12px] space-y-1">
          {warnings.map((w) => (
            <div key={w} className="flex gap-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#F5A971]" />
              <span className="text-white/90">{w}</span>
            </div>
          ))}
        </div>
      )}

      <div className="text-[11px] text-white/50">
        Full go-live checklist lives in <code className="text-white/80">/app/STRIPE_LIVE_DEPLOYMENT.md</code> in the repo.
      </div>
    </Card>
  );
}

function HealthCheck({ ok, label }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {ok ? (
        <CheckCircle2 className="w-4 h-4 text-[#4A6B56]" style={{ color: "#B4E29A" }} />
      ) : (
        <XCircle className="w-4 h-4 text-[#F5A971]" />
      )}
      <span className={ok ? "text-white" : "text-white/60"}>{label}</span>
    </div>
  );
}
